// The browser's one way to this app's own /api routes (docs/bff-routes.md, "Calling a route from the browser"). A call names the
// route by its path (`api.post("/jobs/[id]/moves", { params: { id }, body })`): the body it may send is the route's schema and the
// answer is what the route's handler returns (lib/apiRoutes.ts, generated from app/api), so a call site declares neither. A refusal
// keeps the server's status and body (ApiError): the screens word a 409 differently depending on what was missing. A POST that
// only reads (`api.query`) is asked again as often as its values change and is never treated as a write. A write to a job
// marks its cached reads stale (lib/queryClient.ts); an ended sign-in goes to /login.
import type { NextResponse } from "next/server";
import type { input, ZodType } from "zod";
import { newActionKey, runOnce } from "@/lib/actionKey";
import type { ApiRoutes } from "@/lib/apiRoutes";
import type { ErrorBody } from "@/lib/bffTypes";
import { isRecord } from "@/lib/isRecord";
import type { ParamsOf } from "@/lib/server/bffRoute";
import { markJobChanged } from "@/lib/queryClient";
import { goToLogin, isNotSignedIn } from "@/lib/session/sessionEnded";

const API = "/api";

export class ApiError extends Error {
  /** The refusal's words, where the body has them: a code or sentence in `error`, the API's own in `message` or `detail`. */
  readonly refusal: ErrorBody;

  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
    readonly headers?: Headers
  ) {
    super(message);
    this.name = "ApiError";
    const said = (k: keyof ErrorBody) => (isRecord(body) && typeof body[k] === "string" ? body[k] : undefined);
    this.refusal = { error: said("error"), message: said("message"), detail: said("detail") };
  }
}

export { newActionKey };

/** Whether the route took the call: false when it refused (an ApiError), as `res.ok` was. Anything else is thrown on. */
export async function accepted(call: Promise<unknown>): Promise<boolean> {
  try {
    await call;
    return true;
  } catch (e) {
    if (e instanceof ApiError) return false;
    throw e;
  }
}

// ---- what a route takes and answers, from lib/apiRoutes.ts ---------------------------------------------------------------

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type Write = Exclude<Method, "GET">;

/** The paths that answer `M`. */
export type ApiPath<M extends Method> = {
  [P in keyof ApiRoutes]: M extends keyof ApiRoutes[P] ? P : never;
}[keyof ApiRoutes];

type RouteAt<P extends keyof ApiRoutes, M extends Method> = M extends keyof ApiRoutes[P] ? ApiRoutes[P][M] : never;

/**
 * A refusal's body, as far as types can tell without the status: ErrorBody itself (what `refuse` and the wrapper answer), a
 * Refusal (ErrorBody with anything else), or any body whose `error` is a sentence (`string`). A body whose `error` is a named code
 * (`{ error: "test-mailbox-missing" }`) may be a 200 and stays in the answer.
 */
type IsRefusal<B> = string extends keyof B
  ? B extends ErrorBody
    ? true
    : false
  : B extends { error: infer E }
    ? string extends E
      ? true
      : false
    : [keyof B] extends [never]
      ? false
      : [B] extends [ErrorBody]
        ? [ErrorBody] extends [B]
          ? true
          : false
        : false;
/** The bodies a route answers with on success: each of its handler's bodies but the refusals. */
type Success<B> = B extends unknown ? (IsRefusal<B> extends true ? never : B) : never;

/**
 * What `M` on `P` answers when it succeeds. `unknown` when the route does not say: it answers a file (a Response it builds itself),
 * or its lib/server helper hands the API's answer on untyped.
 */
export type ApiAnswer<P extends keyof ApiRoutes, M extends Method> =
  RouteAt<P, M> extends { handler: (...args: never[]) => Promise<NextResponse<infer B>> }
    ? [Success<B>] extends [never]
      ? unknown
      : Success<B>
    : unknown;

/** The body `M` on `P` takes: its schema's input, else a file (FormData or raw bytes), an empty object or nothing. */
export type ApiBody<P extends keyof ApiRoutes, M extends Write> =
  RouteAt<P, M> extends { body: infer S extends ZodType }
    ? input<S>
    : FormData | Blob | Record<string, never> | undefined;

type QueryValue = string | number | boolean | null | undefined;
type Query = Record<string, QueryValue | readonly QueryValue[]> | URLSearchParams;

type Common = {
  /** Added to the path as `?k=v&…`: a list repeats its key, and a value of null or undefined is left out. */
  query?: Query;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Sees the answer's headers, a refusal's included (an ETag to keep). */
  onHeaders?: (h: Headers) => void;
};

type WithParams<P extends string> = keyof ParamsOf<P> extends never ? { params?: never } : { params: ParamsOf<P> };

type WriteExtras<P extends keyof ApiRoutes, M extends Write> = (undefined extends ApiBody<P, M>
  ? { body?: ApiBody<P, M> }
  : { body: ApiBody<P, M> }) & {
  /**
   * The key of this USER ACTION. A screen that can submit twice (a dialog's button, a retry after a network error) makes one with
   * `newActionKey()` when the action starts and passes it to every submit of it; the BFF forwards it to the API, so the second is a
   * replay, not a second write. Without it each write gets a fresh key.
   */
  idempotencyKey?: string;
  /**
   * Names the action for joining: a second call with the same intent while the first is in flight shares its request and key,
   * and a retry after a refusal reuses the key. A POST is joined by its path and body without one; give one when part of the body
   * changes per click (a timestamp), or to join a PUT or PATCH.
   */
  intent?: string;
};

export type ApiOptions<P extends keyof ApiRoutes, M extends Method> = Common &
  WithParams<P> &
  (M extends Write ? WriteExtras<P, M> : { body?: never });

/** What a read sent as a POST takes: the route's body and the options of a read, none of a write's (no key, no intent). */
export type QueryOptions<P extends keyof ApiRoutes> = Common &
  WithParams<P> &
  (undefined extends ApiBody<P, "POST"> ? { body?: ApiBody<P, "POST"> } : { body: ApiBody<P, "POST"> });

/** The options argument, optional when nothing in it is required. */
type OptionsArg<P extends keyof ApiRoutes, M extends Method> =
  Record<never, never> extends ApiOptions<P, M> ? [options?: ApiOptions<P, M>] : [options: ApiOptions<P, M>];
type QueryArg<P extends keyof ApiRoutes> =
  Record<never, never> extends QueryOptions<P> ? [options?: QueryOptions<P>] : [options: QueryOptions<P>];

// ---- the call --------------------------------------------------------------------------------------------------------------

type Loose = Common & {
  params?: object;
  body?: unknown;
  idempotencyKey?: string;
  intent?: string;
  /** A POST that only reads (`api.query`): none of what makes a request a write. */
  reads?: true;
};

/** `/jobs/[id]/moves` with `{ id }` is `/jobs/<id>/moves`; a `[...path]` takes its segments. */
export function fillPath(path: string, params: object = {}): string {
  const values = new Map<string, unknown>(Object.entries(params));
  return path.replace(/\[(\.\.\.)?(\w+)\]/g, (_, rest: string | undefined, name: string) => {
    const v = values.get(name);
    if (typeof v === "string") return encodeURIComponent(v);
    if (Array.isArray(v)) return v.map((s) => encodeURIComponent(String(s))).join("/");
    throw new Error(`${path}: no value for [${rest ?? ""}${name}]`);
  });
}

function queryString(query: Query | undefined): string {
  if (!query) return "";
  if (query instanceof URLSearchParams) {
    const s = query.toString();
    return s ? `?${s}` : "";
  }
  const pairs = Object.entries(query).flatMap(([k, value]) =>
    (Array.isArray(value) ? value : [value]).flatMap((v: QueryValue) =>
      v === undefined || v === null ? [] : [`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`]
    )
  );
  return pairs.length ? `?${pairs.join("&")}` : "";
}

/**
 * A POST holds ONE key for the same path and body until it succeeds (lib/actionKey.ts), so a double-click or a retry is a replay
 * and two clicks share one request (the BFF keys POSTs only, so PUT/PATCH/DELETE are joined only when the caller names the intent:
 * a later edit must always be sent). Each caller still gets its own `onHeaders` (an ETag) and its own copy of the answer. A file
 * upload has no stable text and gets a fresh key.
 */
async function send<T>(method: Method, url: string, o: Loose, read: (res: Response) => Promise<T>): Promise<T> {
  const isFile = o.body instanceof FormData || o.body instanceof Blob;
  const intent = o.reads
    ? null
    : (o.intent ??
      (method === "POST" && !o.idempotencyKey && !isFile
        ? `${method} ${url} ${JSON.stringify(o.body ?? null)}`
        : null));
  let answer: { value: T; headers: Headers };
  try {
    answer = intent
      ? await runOnce(intent, (idempotencyKey) => request(method, url, { ...o, idempotencyKey }, read))
      : await request(method, url, o, read);
  } catch (e) {
    if (e instanceof ApiError && e.headers) o.onHeaders?.(e.headers);
    throw e;
  }
  o.onHeaders?.(answer.headers);
  return intent ? structuredClone(answer.value) : answer.value;
}

async function request<T>(
  method: Method,
  url: string,
  o: Loose,
  read: (res: Response) => Promise<T>
): Promise<{ value: T; headers: Headers }> {
  const writes = method !== "GET" && !o.reads;
  const job = writes ? url.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0] : undefined;
  const init: RequestInit = { method, cache: "no-store" };
  if (o.signal) init.signal = o.signal;
  const headers: Record<string, string> = { ...(o.headers ?? {}) };
  if (o.body instanceof FormData || o.body instanceof Blob) init.body = o.body;
  else if (o.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(o.body);
  }
  // every write carries the browser's key; the BFF forwards it and mints one only when it is absent
  if (writes && !Object.keys(headers).some((h) => h.toLowerCase() === "idempotency-key"))
    headers["Idempotency-Key"] = o.idempotencyKey ?? newActionKey();
  if (Object.keys(headers).length) init.headers = headers;
  const res = await fetch(url, init);
  // Once the server has the write: a cached read from before it is not shown to a part that mounts after it.
  if (job) markJobChanged(job);
  if (res.ok) return { value: await read(res), headers: res.headers };
  const parsed = await parseText(res);
  // The sign-in has ended (not a secret or a refusal): sign in again and come back to this page.
  if (typeof window !== "undefined" && isNotSignedIn(res.status, parsed)) goToLogin(window.location);
  throw new ApiError(res.status, parsed, `${method} ${url.slice(API.length)} failed: ${res.status}`, res.headers);
}

// A route's answer: a page (text/html) never is one. No route took the call (a server without it answers with its catch-all page).
async function routeAnswer(method: Method, url: string, res: Response): Promise<unknown> {
  if (res.headers.get("content-type")?.startsWith("text/html"))
    throw new ApiError(
      502,
      null,
      `${method} ${url.slice(API.length)} was answered by a page, not its route`,
      res.headers
    );
  return parseText(res);
}

async function parseText(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const urlOf = (path: string, o: Loose) => `${API}${fillPath(path, o.params)}${queryString(o.query)}`;

/** The call for one method: `path` is a route that answers it, `options` what that route takes. */
const call =
  <M extends Method>(method: M) =>
  <P extends ApiPath<M>>(path: P, ...[options]: OptionsArg<P, M>): Promise<ApiAnswer<P, M>> => {
    const o: Loose = options ?? {};
    // The one place an answer meets its type: the route's own handler says what it answers (lib/apiRoutes.ts).
    const url = urlOf(path, o);
    return send(method, url, o, (res) => routeAnswer(method, url, res) as Promise<ApiAnswer<P, M>>);
  };

/**
 * The typed calls. `get` and the writes answer the route's JSON; `file` answers a route that sends a file (a download, a pack)
 * as the Response itself, after the same refusal handling, for `blob()` and its headers.
 */
export const api = {
  get: call("GET"),
  post: call("POST"),
  /**
   * A read whose input is too long for a URL (the draft's readiness, the duplicate check): a POST that evaluates or looks up and
   * writes nothing. Unlike `post` it carries no Idempotency-Key, is never joined with an identical ask (each ask is the answer for
   * the values it was sent), and leaves the job's in-flight and cached reads alone, as a GET does.
   */
  query: <P extends ApiPath<"POST">>(path: P, ...[options]: QueryArg<P>): Promise<ApiAnswer<P, "POST">> => {
    const o: Loose = { ...options, reads: true };
    const url = urlOf(path, o);
    return send("POST", url, o, (res) => routeAnswer("POST", url, res) as Promise<ApiAnswer<P, "POST">>);
  },
  put: call("PUT"),
  patch: call("PATCH"),
  delete: call("DELETE"),
  file: <P extends ApiPath<"GET">>(path: P, ...[options]: OptionsArg<P, "GET">): Promise<Response> => {
    const o: Loose = options ?? {};
    return send("GET", urlOf(path, o), o, async (res) => res);
  },
};
